import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { IdeaPlanMeta, IdeaTask, TaskStatus, Idea } from '@/lib/types';
import { toast } from 'sonner';

interface GeneratedTask {
  title: string;
  description: string;
  task_type: 'experiment' | 'build' | 'research' | 'outreach' | 'admin';
  priority: 'low' | 'medium' | 'high';
  estimated_hours: number;
  start_date: string;
  due_date: string;
  duration_days: number;
  success_criteria: string;
  suggested_position: number;
}

interface GeneratedPlan {
  plan_window: {
    start_date: string;
    end_date: string;
    weeks: number;
  };
  first_irreversible_step: {
    title: string;
    rationale: string;
  };
  tasks: GeneratedTask[];
}

interface PlanIntakeInput {
  hours_per_week: number;
  idea_type: string;
  current_state: string;
  constraint_text: string;
  done_definition: string;
  target_date: string | null;
  plan_start_date: string | null;
  plan_end_date: string | null;
}

export function useIdeaPlan(ideaId: string | null) {
  const { user } = useAuth();
  const [planMeta, setPlanMeta] = useState<IdeaPlanMeta | null>(null);
  const [tasks, setTasks] = useState<IdeaTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchPlan = useCallback(async () => {
    if (!ideaId || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: meta } = await supabase
        .from('idea_plan_meta')
        .select('*')
        .eq('idea_id', ideaId)
        .maybeSingle();

      setPlanMeta(meta as IdeaPlanMeta | null);

      const { data: taskData } = await supabase
        .from('idea_tasks')
        .select('*')
        .eq('idea_id', ideaId)
        .order('position', { ascending: true });

      setTasks((taskData || []) as IdeaTask[]);
    } catch (error) {
      console.error('Error fetching plan:', error);
    } finally {
      setLoading(false);
    }
  }, [ideaId, user]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const createPlan = async (planData: PlanIntakeInput) => {
    if (!ideaId || !user) return null;

    const { data, error } = await supabase
      .from('idea_plan_meta')
      .insert({
        idea_id: ideaId,
        user_id: user.id,
        hours_per_week: planData.hours_per_week,
        idea_type: planData.idea_type,
        current_state: planData.current_state,
        constraint_text: planData.constraint_text,
        done_definition: planData.done_definition,
        target_date: planData.target_date,
        plan_start_date: planData.plan_start_date,
        plan_end_date: planData.plan_end_date,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating plan:', error);
      return null;
    }

    setPlanMeta(data as IdeaPlanMeta);
    return data as IdeaPlanMeta;
  };

  const updatePlan = async (updates: Partial<IdeaPlanMeta>) => {
    if (!ideaId || !user) return false;

    const { error } = await supabase
      .from('idea_plan_meta')
      .update(updates)
      .eq('idea_id', ideaId);

    if (error) {
      console.error('Error updating plan:', error);
      return false;
    }

    setPlanMeta(prev => prev ? { ...prev, ...updates } : null);
    return true;
  };

  const generatePlanWithAI = async (idea: Idea, intake: PlanIntakeInput): Promise<GeneratedPlan | null> => {
    if (!ideaId || !user) {
      console.error('generatePlanWithAI: Missing ideaId or user');
      toast.error('Please select an idea first.');
      return null;
    }

    console.log('generatePlanWithAI: Starting plan generation for idea:', ideaId);
    setGenerating(true);
    
    try {
      // Fetch recent updates for context
      const { data: recentUpdates } = await supabase
        .from('idea_updates')
        .select('reflection, created_at')
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false })
        .limit(3);

      console.log('generatePlanWithAI: Calling edge function with idea:', idea.title);
      
      const { data, error } = await supabase.functions.invoke('generate-plan', {
        body: {
          idea: {
            title: idea.title,
            one_liner: idea.one_liner,
            problem: idea.problem,
            audience: idea.audience,
            proposed_solution: idea.proposed_solution,
            differentiator: idea.differentiator,
          },
          intake: {
            hours_per_week: intake.hours_per_week,
            idea_type: intake.idea_type,
            current_state: intake.current_state,
            constraint_text: intake.constraint_text,
            done_definition: intake.done_definition,
            target_date: intake.target_date,
          },
          recentUpdates: recentUpdates || [],
        },
      });

      console.log('generatePlanWithAI: Edge function response:', { data, error });

      if (error) {
        console.error('Generate plan error:', error);
        
        if (error.message?.includes('429')) {
          toast.error('Rate limit exceeded. Please try again in a moment.');
        } else if (error.message?.includes('402')) {
          toast.error('AI credits exhausted. Please add credits to continue.');
        } else if (error.message?.includes('FunctionsFetchError') || error.message?.includes('Failed to fetch')) {
          toast.error('Could not connect to plan generator. Please try again.');
        } else {
          toast.error('Failed to generate plan. Please try again.');
        }
        return null;
      }

      if (data?.error) {
        console.error('Generate plan returned error:', data.error);
        toast.error(data.error);
        return null;
      }

      // Validate the response has expected structure
      if (!data?.tasks || !Array.isArray(data.tasks)) {
        console.error('Invalid plan response structure:', data);
        toast.error('Received invalid plan data. Please try again.');
        return null;
      }

      console.log('generatePlanWithAI: Plan generated successfully with', data.tasks.length, 'tasks');
      return data as GeneratedPlan;
    } catch (err) {
      console.error('generatePlanWithAI: Unexpected error:', err);
      toast.error('An unexpected error occurred. Please try again.');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const insertGeneratedTasks = async (generatedTasks: GeneratedTask[]) => {
    if (!ideaId || !user) return false;

    const tasksToInsert = generatedTasks.map((task, index) => ({
      idea_id: ideaId,
      user_id: user.id,
      title: task.title,
      description: task.description,
      task_type: task.task_type,
      priority: task.priority,
      estimated_hours: task.estimated_hours,
      start_date: task.start_date,
      due_date: task.due_date,
      duration_days: task.duration_days,
      evidence: task.success_criteria,
      position: task.suggested_position ?? index,
      source: 'ai',
      status: 'next' as const,
    }));

    const { data, error } = await supabase
      .from('idea_tasks')
      .insert(tasksToInsert)
      .select();

    if (error) {
      console.error('Error inserting tasks:', error);
      return false;
    }

    setTasks(prev => [...prev, ...(data as IdeaTask[])]);
    return true;
  };

  const deleteAITasks = async () => {
    if (!ideaId) return false;

    const { error } = await supabase
      .from('idea_tasks')
      .delete()
      .eq('idea_id', ideaId)
      .eq('source', 'ai');

    if (error) {
      console.error('Error deleting AI tasks:', error);
      return false;
    }

    setTasks(prev => prev.filter(t => t.source !== 'ai'));
    return true;
  };

  const regeneratePlan = async (idea: Idea, intake: PlanIntakeInput) => {
    if (!planMeta) return false;

    // Delete existing AI tasks
    await deleteAITasks();

    // Generate new plan
    const generatedPlan = await generatePlanWithAI(idea, intake);
    if (!generatedPlan) return false;

    // Insert new tasks
    const tasksInserted = await insertGeneratedTasks(generatedPlan.tasks);
    if (!tasksInserted) return false;

    // Update plan meta with new version and dates
    const newVersion = (planMeta.plan_version || 1) + 1;
    await updatePlan({
      plan_version: newVersion,
      plan_end_date: generatedPlan.plan_window.end_date,
      first_irreversible_step: generatedPlan.first_irreversible_step.title,
      first_irreversible_rationale: generatedPlan.first_irreversible_step.rationale,
    } as Partial<IdeaPlanMeta>);

    toast.success(`Plan regenerated (v${newVersion})`);
    return true;
  };

  const addTask = async (taskData: Partial<IdeaTask>) => {
    if (!ideaId || !user) return null;

    const maxPosition = tasks.reduce((max, t) => Math.max(max, t.position), -1);

    const { data, error } = await supabase
      .from('idea_tasks')
      .insert({
        idea_id: ideaId,
        user_id: user.id,
        title: taskData.title || 'Untitled Task',
        description: taskData.description,
        status: taskData.status || 'next',
        task_type: taskData.task_type || 'experiment',
        priority: taskData.priority || 'medium',
        position: maxPosition + 1,
        source: 'user',
        ...taskData,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding task:', error);
      return null;
    }

    setTasks(prev => [...prev, data as IdeaTask]);
    return data as IdeaTask;
  };

  const updateTask = async (taskId: string, updates: Partial<IdeaTask>) => {
    const { error } = await supabase
      .from('idea_tasks')
      .update(updates)
      .eq('id', taskId);

    if (error) {
      console.error('Error updating task:', error);
      return false;
    }

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
    return true;
  };

  const deleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('idea_tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      console.error('Error deleting task:', error);
      return false;
    }

    setTasks(prev => prev.filter(t => t.id !== taskId));
    return true;
  };

  const moveTask = async (taskId: string, newStatus: TaskStatus) => {
    return updateTask(taskId, { status: newStatus });
  };

  return {
    planMeta,
    tasks,
    loading,
    generating,
    createPlan,
    updatePlan,
    generatePlanWithAI,
    insertGeneratedTasks,
    regeneratePlan,
    addTask,
    updateTask,
    deleteTask,
    moveTask,
    refetch: fetchPlan,
  };
}